/**
 * EmbeddedApp - Shopify Embedded App Experience
 * 
 * This component renders when the app is loaded inside Shopify Admin.
 * It uses Shopify session tokens for authentication and handles:
 * - Subscription verification
 * - Billing flow (if no active subscription)
 * - Dashboard access (if subscription active)
 * 
 * NO external navigation, NO Google OAuth, NO public navbar.
 */

import { useState, useEffect, useCallback } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useShopify } from '../contexts/ShopifyContext';
import { Loader2, TrendingDown, TrendingUp, CheckCircle2, AlertCircle, ArrowRight, Store, BarChart3, Mail, Zap, Clock, XCircle, Minus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

// API calls use relative paths — same origin as the page
const API_BASE = '';

// Subscription Status Badge Component
const SubscriptionBadge = ({ status, trialDaysRemaining, plan }) => {
  if (status === 'active') {
    return (
      <div data-testid="subscription-badge-active" className="flex items-center gap-2 bg-green-500/10 border border-green-500/20 rounded-full px-3 py-1">
        <CheckCircle2 className="w-4 h-4 text-green-400" />
        <span className="text-xs font-medium text-green-400">Active</span>
        {plan && <span className="text-xs text-green-400/70">({plan})</span>}
      </div>
    );
  }
  
  if (status === 'trialing') {
    return (
      <div data-testid="subscription-badge-trialing" className="flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/20 rounded-full px-3 py-1">
        <Clock className="w-4 h-4 text-yellow-400" />
        <span className="text-xs font-medium text-yellow-400">
          Trial ({trialDaysRemaining || 7} days left)
        </span>
      </div>
    );
  }
  
  if (status === 'cancelled' || status === 'canceled') {
    return (
      <div data-testid="subscription-badge-cancelled" className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-full px-3 py-1">
        <XCircle className="w-4 h-4 text-red-400" />
        <span className="text-xs font-medium text-red-400">Cancelled</span>
      </div>
    );
  }
  
  return (
    <div data-testid="subscription-badge-none" className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3 py-1">
      <AlertCircle className="w-4 h-4 text-text-muted" />
      <span className="text-xs font-medium text-text-muted">No subscription</span>
    </div>
  );
};

// Follow-Up Display Component
const FollowUpBanner = ({ followUp }) => {
  if (!followUp) return null;
  
  const { previous_value, current_value, delta_percent, status, metric_label } = followUp;
  
  const formatValue = (val, metric) => {
    if (metric === 'Revenue' || metric === 'AOV' || metric === 'Top Product Revenue') {
      return `$${val.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    }
    if (metric === 'Returning Customer Rate') {
      return `${val}%`;
    }
    return val.toLocaleString();
  };
  
  const colorClass = status === 'improved' 
    ? 'text-green-400 bg-green-500/10 border-green-500/20' 
    : status === 'declined' 
      ? 'text-red-400 bg-red-500/10 border-red-500/20' 
      : 'text-zinc-400 bg-white/5 border-white/10';
  
  const StatusIcon = status === 'improved' ? TrendingUp : status === 'declined' ? TrendingDown : Minus;
  
  return (
    <div data-testid="follow-up-banner" className={`mt-3 rounded-lg border px-4 py-2.5 ${colorClass}`}>
      <div className="flex items-center gap-2">
        <StatusIcon className="w-4 h-4 flex-shrink-0" />
        <span className="text-sm font-medium">
          Last Week Update: {metric_label || 'Metric'} moved from {formatValue(previous_value, metric_label)} → {formatValue(current_value, metric_label)} ({delta_percent > 0 ? '+' : ''}{delta_percent}%)
        </span>
      </div>
    </div>
  );
};

// Embedded Dashboard Component
const EmbeddedDashboard = ({ shopData, subscriptionData, onRefresh, authenticatedFetch }) => {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [baselineGenerating, setBaselineGenerating] = useState(false);

  const fetchInsights = async () => {
    try {
      const res = await authenticatedFetch(
        `${API_BASE}/api/shopify/embedded/insights?shop=${encodeURIComponent(shopData.shop_domain)}`
      );
      const data = await res.json();
      setInsights(data.insights || []);
      return data.insights || [];
    } catch (error) {
      console.error('Failed to fetch insights:', error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!shopData?.shop_domain) return;
    
    const init = async () => {
      const results = await fetchInsights();
      
      // If just activated and no insights, trigger baseline
      if (results.length === 0 && (subscriptionData?.justActivated || subscriptionData?.baseline_pending)) {
        setBaselineGenerating(true);
        try {
          await authenticatedFetch(
            `${API_BASE}/api/shopify/embedded/trigger-baseline?shop=${encodeURIComponent(shopData.shop_domain)}`,
            { method: 'POST' }
          );
        } catch (e) {
          console.error('Baseline trigger failed:', e);
        }
        
        // Poll for results every 5 seconds for up to 2 minutes
        let attempts = 0;
        const poll = setInterval(async () => {
          attempts++;
          try {
            const res = await authenticatedFetch(
              `${API_BASE}/api/shopify/embedded/insights?shop=${encodeURIComponent(shopData.shop_domain)}`
            );
            const data = await res.json();
            if (data.insights?.length > 0) {
              setInsights(data.insights);
              setBaselineGenerating(false);
              clearInterval(poll);
            } else if (attempts >= 24) {
              setBaselineGenerating(false);
              clearInterval(poll);
            }
          } catch (e) {
            if (attempts >= 24) {
              setBaselineGenerating(false);
              clearInterval(poll);
            }
          }
        }, 5000);
        
        return () => clearInterval(poll);
      }
    };
    
    init();
  }, [shopData, authenticatedFetch, subscriptionData]);

  return (
    <div data-testid="embedded-dashboard" className="min-h-screen bg-[#050505] text-white p-6">
      {/* Header - No public nav */}
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg overflow-hidden">
              <img src="/favicon.png" alt="ReplyDriven" className="w-full h-full object-cover" />
            </div>
            <div>
              <h1 className="font-heading text-xl font-bold">ReplyDriven</h1>
              <p data-testid="shop-domain" className="text-sm text-text-muted">{shopData?.shop_domain}</p>
            </div>
          </div>
          
          {/* Subscription Status Badge */}
          <SubscriptionBadge 
            status={subscriptionData?.status} 
            trialDaysRemaining={subscriptionData?.trial_days_remaining}
            plan={subscriptionData?.plan}
          />
        </div>

        {/* Trial Notice Banner */}
        {subscriptionData?.status === 'trialing' && (
          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-yellow-400 flex-shrink-0" />
              <div>
                <p className="text-yellow-400 font-medium">
                  Trial period: {subscriptionData.trial_days_remaining || 7} days remaining
                </p>
                <p className="text-yellow-400/70 text-sm">
                  Your subscription will automatically begin after the trial. No action needed.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Post-Subscription Success State */}
        {subscriptionData?.justActivated && (
          <div data-testid="subscription-success" className="bg-surface border border-[#D4F34A]/20 rounded-xl p-6 mb-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-6 h-6 text-green-400" />
              </div>
              <div className="flex-1">
                <h2 className="font-heading text-lg font-bold text-white mb-1">
                  You&apos;re subscribed. Your store is now being monitored.
                </h2>
                <p className="text-text-muted text-sm mb-4">
                  We analyze your store weekly and flag the single most financially dangerous trend before it compounds.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="bg-[#1E1E1E] rounded-lg p-3">
                    <p className="text-xs text-text-muted mb-1">Next analysis</p>
                    <p className="text-sm font-medium text-white">Monday 9 AM UTC</p>
                  </div>
                  <div className="bg-[#1E1E1E] rounded-lg p-3">
                    <p className="text-xs text-text-muted mb-1">Metrics tracked</p>
                    <p className="text-sm font-medium text-white">Revenue, AOV, Orders, Return rate</p>
                  </div>
                  <div className="bg-[#1E1E1E] rounded-lg p-3">
                    <p className="text-xs text-text-muted mb-1">Follow-up tracking</p>
                    <p className="text-sm font-medium text-white">Measured weekly</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Insights Section */}
        <div className="space-y-6">
          <h2 className="font-heading text-lg font-semibold">Recent Insights</h2>
          
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#D4F34A] animate-spin" />
            </div>
          ) : baselineGenerating ? (
            <div data-testid="baseline-generating" className="bg-surface border border-[#D4F34A]/20 rounded-xl p-8 text-center">
              <Loader2 className="w-12 h-12 text-[#D4F34A] animate-spin mx-auto mb-4" />
              <h3 className="font-semibold text-white mb-2">Generating Your Initial Baseline Report</h3>
              <p className="text-text-muted text-sm mb-4">
                We&apos;re analyzing your store data right now. This usually takes 30-60 seconds.
              </p>
              <div className="flex items-center justify-center gap-2 text-xs text-text-muted">
                <div className="w-2 h-2 rounded-full bg-[#D4F34A] animate-pulse" />
                <span>Fetching orders, computing metrics, running AI analysis...</span>
              </div>
            </div>
          ) : insights.length > 0 ? (
            <div className="space-y-4">
              {/* Baseline report banner */}
              {insights[0]?.report_id && subscriptionData?.justActivated && (
                <div data-testid="baseline-report-banner" className="bg-[#D4F34A]/5 border border-[#D4F34A]/20 rounded-lg px-4 py-3 mb-2">
                  <p className="text-[#D4F34A] text-sm font-medium">Initial Baseline Report - Your starting point for weekly tracking</p>
                </div>
              )}
              {insights.map((insight, index) => (
                <div key={insight.report_id || index} data-testid={`insight-card-${index}`} className="bg-surface border border-white/5 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#D4F34A]/10 flex items-center justify-center flex-shrink-0">
                      <Zap className="w-5 h-5 text-[#D4F34A]" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-white mb-2">{insight.title || 'Weekly Insight'}</h3>
                      <p className="text-text-muted text-sm">{insight.summary || insight.insight}</p>
                      {insight.recommended_action && (
                        <p className="text-[#D4F34A] text-sm mt-2 font-medium">
                          Action: {insight.recommended_action}
                        </p>
                      )}
                      {insight.target_metric && insight.baseline_value != null && (
                        <p className="text-text-muted text-xs mt-2">
                          Tracking: {insight.target_metric.replace(/_/g, ' ')} (baseline: {
                            ['revenue', 'aov', 'top_product_revenue'].includes(insight.target_metric)
                              ? `$${insight.baseline_value.toLocaleString()}`
                              : insight.target_metric === 'returning_customer_rate'
                                ? `${insight.baseline_value}%`
                                : insight.baseline_value.toLocaleString()
                          })
                        </p>
                      )}
                      <FollowUpBanner followUp={insight.follow_up} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-surface border border-white/5 rounded-xl p-8 text-center">
              <BarChart3 className="w-12 h-12 text-text-muted mx-auto mb-4" />
              <h3 className="font-semibold text-white mb-2">No insights yet</h3>
              <p className="text-text-muted text-sm">
                Your first weekly insight will be generated on Monday at 9 AM UTC.
              </p>
            </div>
          )}
        </div>

        {/* Feature Summary */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-surface border border-white/5 rounded-xl p-4">
            <Mail className="w-6 h-6 text-[#D4F34A] mb-2" />
            <h4 className="font-semibold text-white text-sm">Weekly Reports</h4>
            <p className="text-text-muted text-xs">Every Monday at 9 AM UTC</p>
          </div>
          <div className="bg-surface border border-white/5 rounded-xl p-4">
            <BarChart3 className="w-6 h-6 text-[#D4F34A] mb-2" />
            <h4 className="font-semibold text-white text-sm">AI Analysis</h4>
            <p className="text-text-muted text-xs">Powered by GPT-5.2</p>
          </div>
          <div className="bg-surface border border-white/5 rounded-xl p-4">
            <Store className="w-6 h-6 text-[#D4F34A] mb-2" />
            <h4 className="font-semibold text-white text-sm">Shopify Native</h4>
            <p className="text-text-muted text-xs">Managed via Shopify Admin</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Upgrade Screen (shown when no active subscription)
const UpgradeScreen = ({ shopData, onSubscribe, isLoading }) => {
  const [selectedPlan, setSelectedPlan] = useState('operator');

  const plans = [
    {
      id: 'operator',
      name: 'Operator',
      price: 149,
      description: 'Weekly profit oversight for your store',
      features: [
        '1 Shopify store',
        'Weekly profit insight + follow-up tracking',
        'Dashboard access',
        'Email reports every Monday',
        'Insight history'
      ],
      popular: true
    },
    {
      id: 'growth',
      name: 'Growth',
      price: 299,
      description: 'For operators running multiple stores',
      features: [
        'Up to 5 Shopify stores',
        'Weekly profit insight per store',
        'Consolidated weekly digest',
        'Per-store email reports',
        'Insight history'
      ]
    }
  ];

  return (
    <div data-testid="upgrade-screen" className="min-h-screen bg-[#050505] text-white p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl overflow-hidden mx-auto mb-4">
            <img src="/favicon.png" alt="ReplyDriven" className="w-full h-full object-cover" />
          </div>
          <h1 className="font-heading text-2xl font-bold mb-2">Start Your Free Trial</h1>
          <p className="text-text-muted">
            7-day free trial • Cancel anytime in Shopify Admin
          </p>
          {shopData?.shop_domain && (
            <p className="text-sm text-text-muted mt-2">
              Store: {shopData.shop_domain}
            </p>
          )}
        </div>

        {/* Plan Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {plans.map((plan) => (
            <button
              key={plan.id}
              onClick={() => setSelectedPlan(plan.id)}
              className={`relative bg-surface border-2 rounded-xl p-6 text-left transition-all ${
                selectedPlan === plan.id 
                  ? 'border-[#D4F34A]' 
                  : 'border-white/10 hover:border-white/20'
              }`}
            >
              {plan.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4F34A] text-black text-xs font-bold px-3 py-1 rounded-full">
                  RECOMMENDED
                </span>
              )}
              
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg font-bold">{plan.name}</h3>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  selectedPlan === plan.id 
                    ? 'border-[#D4F34A] bg-[#D4F34A]' 
                    : 'border-white/30'
                }`}>
                  {selectedPlan === plan.id && (
                    <CheckCircle2 className="w-4 h-4 text-black" />
                  )}
                </div>
              </div>
              
              <p className="text-text-muted text-sm mb-4">{plan.description}</p>
              
              <div className="mb-4">
                <span className="font-heading text-3xl font-bold">${plan.price}</span>
                <span className="text-text-muted">/month</span>
              </div>
              
              <ul className="space-y-2">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-[#D4F34A] flex-shrink-0" />
                    <span className="text-text-muted">{feature}</span>
                  </li>
                ))}
              </ul>
            </button>
          ))}
        </div>

        {/* Subscribe Button */}
        <Button
          onClick={() => onSubscribe(selectedPlan)}
          disabled={isLoading}
          className="w-full bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold py-6 text-lg"
          data-testid="embedded-subscribe-btn"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Creating subscription...
            </>
          ) : (
            <>
              Start 7-Day Free Trial <ArrowRight className="w-5 h-5 ml-2" />
            </>
          )}
        </Button>

        <p className="text-center text-xs text-text-muted mt-4">
          Subscriptions are managed through Shopify's billing system.
          <br />
          Cancel anytime from your Shopify Admin.
        </p>
      </div>
    </div>
  );
};

// Main Embedded App Component
const EmbeddedApp = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isEmbedded, isReady, host, shop: shopParam, sessionToken, tokenError, authenticatedFetch, refreshToken } = useShopify();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shopData, setShopData] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [justActivated, setJustActivated] = useState(false);

  // Get params from URL
  const params = new URLSearchParams(location.search);
  const shop = shopParam || params.get('shop');
  const hostParam = host || params.get('host');
  const billingStatus = params.get('billing');
  const billingPlan = params.get('plan');

  // Handle billing=success (fallback for non-embedded redirect)
  useEffect(() => {
    if (billingStatus === 'success') {
      console.log('Billing success detected via URL, will refetch subscription');
      setJustActivated(true);
      
      // Clear the billing params from URL
      searchParams.delete('billing');
      searchParams.delete('plan');
      setSearchParams(searchParams, { replace: true });
    } else if (billingStatus === 'declined') {
      toast.info('Subscription was not approved.');
      searchParams.delete('billing');
      setSearchParams(searchParams, { replace: true });
    } else if (billingStatus === 'error') {
      const reason = params.get('reason');
      toast.error(`Billing error: ${reason || 'Unknown error'}`);
      searchParams.delete('billing');
      searchParams.delete('reason');
      setSearchParams(searchParams, { replace: true });
    }
  }, [billingStatus, searchParams, setSearchParams]);

  // Verify Shopify session and get shop data
  const verifySession = useCallback(async (forceRefresh = false) => {
    if (!shop) {
      setError('No shop parameter provided. Please access this app through your Shopify Admin.');
      setLoading(false);
      return;
    }

    // HARD GATE: Do NOT call verify without a token
    if (!sessionToken && isEmbedded) {
      console.log('[VERIFY_FE] No sessionToken yet — skipping verify call, waiting for App Bridge');
      return; // The useEffect will re-trigger when sessionToken changes
    }

    const makeVerifyCall = async (token) => {
      const hasToken = !!token;
      const url = `${API_BASE}/api/shopify/embedded/verify?shop=${encodeURIComponent(shop)}&host=${encodeURIComponent(hostParam || '')}`;
      console.log(`[VERIFY_FE] Calling verify with token: ${hasToken ? 'YES' : 'NO'}`);
      
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const res = await fetch(url, { headers });
      const body = await res.json().catch(() => ({}));
      console.log(`[VERIFY_FE] Verify response status: ${res.status}`);
      console.log(`[VERIFY_FE] Verify response body:`, JSON.stringify(body));
      return { res, body };
    };

    try {
      // Step 1: Get a fresh token
      let token = sessionToken;
      if (!token || forceRefresh) {
        console.log('[VERIFY_FE] Refreshing token before verify...');
        token = await refreshToken();
      }
      
      if (!token) {
        console.log('[VERIFY_FE] Still no token after refresh — aborting verify');
        setError('Unable to get session token. Please close and reopen the app from Shopify Admin.');
        setLoading(false);
        return;
      }

      // Step 2: Call verify
      let { res, body } = await makeVerifyCall(token);
      
      // Step 3: If 401 and reason is missing/expired token, retry ONCE with fresh token
      if (res.status === 401) {
        const detail = (body.detail || '').toLowerCase();
        const isTokenIssue = detail.includes('missing') || detail.includes('expired') || detail.includes('token');
        
        if (isTokenIssue) {
          console.log(`[VERIFY_FE] Got 401 (${body.detail}) — retrying with fresh token`);
          token = await refreshToken();
          if (token) {
            ({ res, body } = await makeVerifyCall(token));
          }
        }
      }

      // Step 4: Handle final response
      if (!res.ok) {
        if (res.status === 401) {
          const detail = (body.detail || '').toLowerCase();
          // Only show "auth failed" for real errors, NOT for missing token
          if (detail.includes('missing') || detail.includes('no credentials')) {
            console.log('[VERIFY_FE] Token still missing after retry — will retry on next render');
            // Don't set error — let the component retry when sessionToken updates
            setLoading(false);
            return;
          }
          // Real auth failure: invalid signature, store not found, etc.
          console.error(`[VERIFY_FE] Authentication failed: ${body.detail}`);
          setError(body.detail || 'Authentication failed. Please reinstall the app.');
          setLoading(false);
          return;
        }
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      
      console.log('[VERIFY_FE] Session verified successfully');
      
      setShopData(body.shop);
      
      // Use just_activated from backend or from URL param
      const wasJustActivated = body.subscription?.just_activated || justActivated;
      if (wasJustActivated && !justActivated) {
        setJustActivated(true);
        toast.success('Subscription activated successfully!');
      }
      
      setSubscriptionData({
        ...body.subscription,
        justActivated: wasJustActivated
      });
      setError(null);
    } catch (err) {
      console.error('[VERIFY_FE] Session verification failed:', err);
      setError(err.message || 'Failed to verify session. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [shop, hostParam, sessionToken, isEmbedded, refreshToken, justActivated]);

  // Verify session ONLY when we have a token (or not embedded)
  useEffect(() => {
    // For embedded: require both isReady AND sessionToken
    // For non-embedded: just isReady
    const canVerify = isReady && (isEmbedded ? !!sessionToken : true);
    
    if (canVerify) {
      console.log(`[VERIFY_FE] useEffect triggered: isReady=${isReady}, isEmbedded=${isEmbedded}, hasToken=${!!sessionToken}`);
      verifySession(billingStatus === 'success');
    } else {
      console.log(`[VERIFY_FE] useEffect waiting: isReady=${isReady}, isEmbedded=${isEmbedded}, hasToken=${!!sessionToken}`);
    }
  }, [isReady, sessionToken, isEmbedded, verifySession, billingStatus]);

  // Handle subscription
  const handleSubscribe = async (plan) => {
    setIsSubscribing(true);
    
    try {
      // Refresh token before important action
      await refreshToken();
      
      console.log(`🛒 Creating subscription for plan: ${plan}`);
      
      const res = await authenticatedFetch(`${API_BASE}/api/billing/shopify/create-subscription`, {
        method: 'POST',
        body: JSON.stringify({
          plan,
          shop,
          embedded: true,
          host: hostParam
        })
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${res.status}`);
      }
      
      const data = await res.json();
      
      if (data.confirmation_url) {
        console.log(`🔗 Redirecting to Shopify billing: ${data.confirmation_url}`);
        // Redirect to Shopify billing approval
        window.top.location.href = data.confirmation_url;
      } else {
        toast.error('Failed to create subscription. Please try again.');
        setIsSubscribing(false);
      }
    } catch (err) {
      console.error('Subscription creation failed:', err);
      toast.error(err.message || 'Failed to create subscription. Please try again.');
      setIsSubscribing(false);
    }
  };

  // Show token error
  if (tokenError && isEmbedded) {
    return (
      <div data-testid="embedded-token-error" className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="font-heading text-xl font-bold text-white mb-2">Session Error</h2>
          <p className="text-text-muted mb-4">{tokenError}</p>
          <p className="text-text-muted text-sm mb-6">
            Please try closing and reopening the app from your Shopify Admin.
          </p>
          <Button
            data-testid="reload-app-btn"
            onClick={() => window.location.reload()}
            className="bg-white/10 text-white hover:bg-white/20"
          >
            Reload App
          </Button>
        </div>
      </div>
    );
  }

  // Loading state - waiting for App Bridge
  if (!isReady || (isEmbedded && !sessionToken && !tokenError)) {
    return (
      <div data-testid="embedded-loading-bridge" className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-[#D4F34A] animate-spin mx-auto mb-4" />
          <p className="text-text-muted">Initializing Shopify App Bridge...</p>
        </div>
      </div>
    );
  }

  // Loading state - verifying session
  if (loading) {
    return (
      <div data-testid="embedded-loading-verify" className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-[#D4F34A] animate-spin mx-auto mb-4" />
          <p className="text-text-muted">Loading ReplyDriven...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div data-testid="embedded-error" className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="font-heading text-xl font-bold text-white mb-2">Something went wrong</h2>
          <p data-testid="error-message" className="text-text-muted mb-6">{error}</p>
          <Button
            data-testid="try-again-btn"
            onClick={() => window.location.reload()}
            className="bg-white/10 text-white hover:bg-white/20"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  // Check subscription status - ACTIVE or TRIALING both get dashboard access
  const hasActiveSubscription = subscriptionData?.status === 'active' || subscriptionData?.status === 'trialing';

  // If no active subscription (and not trialing), show upgrade screen
  if (!hasActiveSubscription) {
    return (
      <UpgradeScreen
        shopData={shopData}
        onSubscribe={handleSubscribe}
        isLoading={isSubscribing}
      />
    );
  }

  // Show embedded dashboard for ACTIVE or TRIALING subscriptions
  return (
    <EmbeddedDashboard
      shopData={shopData}
      subscriptionData={subscriptionData}
      onRefresh={verifySession}
      authenticatedFetch={authenticatedFetch}
    />
  );
};

export default EmbeddedApp;
